import { type FC, useMemo } from 'react'
import type { GetStaticProps, InferGetStaticPropsType } from 'next'
import { type DehydratedState, QueryClient, dehydrate } from '@tanstack/react-query'

import { ResourcesView } from 'views/Resources'

import { createClient } from '@segment/api/staticSiteBackend/client'
import { createClient as createAlgoliaClient } from '@segment/api/algolia/client'
import {
  createFeaturedResourcesQuery,
  createFiltersQuery,
  createResourcesQuery,
} from '@segment/api/routes/resources/queries'

import {
  AlgoliaClientProvider,
  createResourcesIndex,
} from '@segment/api/routes/resources/contexts/algoliaClientContext'

import { createStaticSiteBackend, StaticSiteBackendProvider } from '@segment/contexts/staticSiteBackend'

import type { AppExternalPageProps } from '@segment/next-shared/Providers'

const Resources: FC<InferGetStaticPropsType<typeof getStaticProps>> = () => {
  const staticSiteBackend = useMemo(() => createStaticSiteBackend(), [])
  const algoliaClient = useMemo(() => createAlgoliaClient(), [])
  return (
    <StaticSiteBackendProvider value={staticSiteBackend}>
      <AlgoliaClientProvider value={algoliaClient}>
        <ResourcesView />
      </AlgoliaClientProvider>
    </StaticSiteBackendProvider>
  )
}

export default Resources

export const getStaticProps: GetStaticProps<AppExternalPageProps> = async () => {
  const queryClient = new QueryClient()
  const client = createClient()
  const algoliaClient = createAlgoliaClient()
  const searchIndex = createResourcesIndex(algoliaClient)

  await queryClient.prefetchInfiniteQuery(
    ...createResourcesQuery(searchIndex, {
      query: ``,
      filters: ``,
    }),
    { staleTime: Infinity }
  )

  await queryClient.prefetchQuery(...createFiltersQuery(client), { staleTime: Infinity })

  await queryClient.prefetchQuery(...createFeaturedResourcesQuery(client, { max: 3 }), { staleTime: Infinity })

  const dangerousDehydratedState = dehydrate(queryClient)

  const safeDehydratedState: DehydratedState = JSON.parse(JSON.stringify(dangerousDehydratedState))

  const props: AppExternalPageProps = {
    dehydratedState: safeDehydratedState,
  }

  return {
    props,
  }
}
